import { Injectable } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';
import { filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { NamApiResponse } from 'src/app/home/object-info/model/nam-api-response.interface';
import { NamObjectTemplate } from 'src/app/home/object-info/model/nam-object-template.interface';
import {
  RelatedItemInterface,
  RelatedItemsInterface,
} from 'src/app/home/object-info/model/related-item.interface';
import { NamService } from './nam.service';
@Injectable({
  providedIn: 'root',
})
export class NamSelectedObjectService {
  // public selectedObjectChanged = new Subject<string>(); //The urn of the object
  public highlightedObject: RelatedItemInterface;

  // Observable of the object selected to view information details
  private readonly _objectForInfoSource$ = new ReplaySubject<RelatedItemInterface>(1);
  private readonly _selectedObjectForInfoObservable$ = this._objectForInfoSource$.asObservable();
  get selectedObjectForInfo$(): Observable<RelatedItemInterface> {
    return this._selectedObjectForInfoObservable$;
  }
  set selectedObjectForInfo(selectedObject: RelatedItemInterface) {
    this._objectForInfoSource$.next(selectedObject);
    // this.selectedObjectChanged.next(selectedObject.urn);
  }

  // Observable Of selected Index from History dropdown
  private readonly _selectedIndexFromObjectHistorySource$ = new BehaviorSubject<number>(0);
  readonly selectedIndexFromObjectHistory$ = this._selectedIndexFromObjectHistorySource$.asObservable();
  set clearHistory(index: number) {
    this._selectedIndexFromObjectHistorySource$.next(index);
  }

  // Observable of the selected template to display the image on MAP
  private readonly _objectTemplateSource$ = new ReplaySubject<NamObjectTemplate>(1);
  private readonly _previewSelectedObjectTemplateObservable$ = this._objectTemplateSource$.asObservable();
  get previewSelectedObjectTemplate$(): Observable<NamObjectTemplate> {
    return this._previewSelectedObjectTemplateObservable$;
  }
  set previewSelectedObjectTemplate(selectedObjectTemplate: NamObjectTemplate) {
    this._objectTemplateSource$.next(selectedObjectTemplate);
  }

  // Observable of the selected object to EDIT its geometry on the map
  private readonly _objectForEditGeometrySource = new Subject<RelatedItemInterface>();
  private readonly _selectedObjectForEditGeometryObservable$ =
    this._objectForEditGeometrySource.asObservable();
  get selectedObjectForEditGeometry$(): Observable<RelatedItemInterface> {
    return this._selectedObjectForEditGeometryObservable$;
  }
  set selectedObjectForEditGeometry(selectedObject: RelatedItemInterface) {
    this._objectForEditGeometrySource.next(selectedObject);
  }

  // Observable of the selected object to HIGHLIGHT its geometry on the map
  private readonly _objectToHighlightSource = new Subject<RelatedItemInterface>();
  private readonly _selectedObjectToHighlightObservable$ = this._objectToHighlightSource.asObservable();
  get selectedObjectToHighlight$(): Observable<RelatedItemInterface> {
    return this._selectedObjectToHighlightObservable$;
  }
  set selectedObjectToHighlight(selectedObject: RelatedItemInterface) {
    this.highlightedObject = selectedObject;
    this._objectToHighlightSource.next(selectedObject);
  }

  // Observable for the unselect object from HIGHLIGHT or EDIT its geometry on the map
  private readonly _objectDeselectSource = new Subject<RelatedItemInterface>();
  private readonly _deselectedObjectObservable$ = this._objectDeselectSource.asObservable();
  get deselectedObject$(): Observable<RelatedItemInterface> {
    return this._deselectedObjectObservable$;
  }
  set deselectedObject(selectedObject: RelatedItemInterface) {
    this.highlightedObject = null;
    this._objectDeselectSource.next(selectedObject);
  }

  // Observale set in the component for the INSERT button from template modal, insert template
  //  todo: get , set ?
  clickStream$: Observable<PointerEvent>;

  constructor(private namService: NamService) {}

  getTemplatesForSelectedObject() {
    return this.selectedObjectForInfo$.pipe(switchMap((item) => this.namService.getTemplates(item.urn)));
  }

  getSelectedObjectByUrnAndRelatedItems(): Observable<{
    response: NamApiResponse<{}>;
    relatedItems: RelatedItemsInterface;
  }> {
    return this.selectedObjectForInfo$.pipe(
      switchMap((item) => this.namService.getObjectByUrnAndRelatedItems(item.urn))
    );
  }

  getImageForSelectedTemplate(): Observable<null | SafeUrl> {
    return this.previewSelectedObjectTemplate$.pipe(
      switchMap((template) => this.namService.getTemplateImage(template.name, template.collection))
    );
  }

  getObjectTemplateToInsert(): Observable<NamObjectTemplate> {
    return this.clickStream$
      .pipe(withLatestFrom(this.previewSelectedObjectTemplate$))
      .pipe(map(([pointerEvent, namObjectTemplate]) => namObjectTemplate));
  }

  selectedObjectToEditWithDetailsAndGeometry$ = this.selectedObjectForEditGeometry$.pipe(
    filter((relatedItem) => !!relatedItem.urn),
    switchMap((relatedItem) => this.namService.selectObjectByUrn(relatedItem.urn))
  );

  selectedObjectToHighlightWithDetailsAndGeometry$ = this.selectedObjectToHighlight$.pipe(
    filter((relatedItem) => !!relatedItem.urn),
    switchMap((relatedItem) => this.namService.selectObjectByUrn(relatedItem.urn))
  );
}
